import { useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, ArrowUpRight, Star } from "lucide-react";
import { Reveal } from "@/components/site/Motion";
import { api } from "@/lib/api";
import { T } from "@/context/ContentContext";
import { useContact } from "@/hooks/useContact";

function ReviewCard({ r, featured }) {
  return (
    <article data-testid={`review-card-${r.id}`}
      className={`snap-start shrink-0 w-[85%] sm:w-[360px] lg:w-[380px] flex flex-col justify-between rounded-[24px] p-6 ${featured ? "bg-wine text-cream" : "bg-paper text-ink"}`}>
      <div>
        <span className="flex gap-0.5">{Array.from({ length: r.rating || 5 }).map((_, k) => <Star key={k} className="h-4 w-4 text-gold fill-gold" />)}</span>
        <p className="mt-4 font-serif text-xl leading-snug line-clamp-5">"{r.text}"</p>
      </div>
      <div className="mt-6 flex items-center justify-between gap-3">
        <p className="font-display font-bold">{r.name}</p>
        {r.trip && <span className={`eyebrow rounded-full px-3 py-1 ${featured ? "bg-cream/15" : "bg-cream"}`}>{r.trip}</span>}
      </div>
    </article>
  );
}

export function ReviewsSection() {
  const CONTACT = useContact();
  const [reviews, setReviews] = useState([]);
  const track = useRef(null);

  useEffect(() => {
    api.get("/reviews").then((r) => setReviews(Array.isArray(r.data) ? r.data : [])).catch(() => setReviews([]));
  }, []);

  if (!reviews.length) return null;
  const scroll = (dir) => track.current?.scrollBy({ left: dir * 400, behavior: "smooth" });
  const btn = "grid place-items-center h-11 w-11 rounded-full border border-ink/20 hover:bg-ink hover:text-cream transition-colors";

  return (
    <section className="bg-cream py-16 sm:py-20" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal className="flex flex-col sm:flex-row sm:items-end justify-between gap-5 mb-8">
          <div>
            <T k="reviews.eyebrow" as="p" className="eyebrow text-terra mb-2">5-star on Google Maps</T>
            <T k="reviews.title" as="h2" className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">Straight from our travellers.</T>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => scroll(-1)} aria-label="Previous reviews" data-testid="reviews-prev" className={btn}><ArrowLeft className="h-4 w-4" /></button>
            <button onClick={() => scroll(1)} aria-label="Next reviews" data-testid="reviews-next" className={btn}><ArrowRight className="h-4 w-4" /></button>
            <a href={CONTACT.googleReviews} target="_blank" rel="noreferrer" data-testid="reviews-google-link"
              className="group ml-2 inline-flex items-center gap-2 rounded-full bg-ink text-cream px-5 py-3 text-sm font-semibold hover:bg-terra transition-colors">
              <T k="reviews.google">Google reviews</T> <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" />
            </a>
          </div>
        </Reveal>
        <div ref={track} data-lenis-prevent data-testid="reviews-track"
          className="flex gap-4 overflow-x-auto snap-x snap-mandatory scroll-pl-5 sm:scroll-pl-0 no-scrollbar -mx-5 px-5 sm:mx-0 sm:px-0 pb-2">
          {reviews.map((r, i) => <ReviewCard key={r.id} r={r} featured={i === 0} />)}
        </div>
      </div>
    </section>
  );
}
